import type { CalendarDate, Instant } from "@hq/contracts";

/** The current instant and zone, passed explicitly so date logic is deterministic in tests. */
export type NowContext = {
  now: Instant;
  timeZone: string;
};

export function nowContext(
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): NowContext {
  return { now: new Date().toISOString(), timeZone };
}

export function toInstant(d: Date): Instant {
  return d.toISOString();
}

const dateFormatters = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = dateFormatters.get(timeZone);
  if (!f) {
    // en-CA formats as YYYY-MM-DD.
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    dateFormatters.set(timeZone, f);
  }
  return f;
}

/** The calendar date an instant falls on in the given zone. */
export function calendarDateOf(instant: Instant | Date, timeZone: string): CalendarDate {
  const d = typeof instant === "string" ? new Date(instant) : instant;
  return formatter(timeZone).format(d);
}

export function todayOf(ctx: NowContext): CalendarDate {
  return calendarDateOf(ctx.now, ctx.timeZone);
}

function partsOf(date: CalendarDate): [number, number, number] {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  const d = Number(date.slice(8, 10));
  return [y, m, d];
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function makeCalendarDate(y: number, m: number, d: number): CalendarDate {
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function addDays(date: CalendarDate, days: number): CalendarDate {
  const [y, m, d] = partsOf(date);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return makeCalendarDate(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** Whole days from `a` to `b` (positive when `b` is later). */
export function daysBetween(a: CalendarDate, b: CalendarDate): number {
  const [ay, am, ad] = partsOf(a);
  const [by, bm, bd] = partsOf(b);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

/** Lexicographic comparison is correct for ISO dates and instants. */
export function compareIso(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** 0 = Sunday. */
export function weekdayOf(date: CalendarDate): number {
  const [y, m, d] = partsOf(date);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function isValidCalendarDate(s: string): s is CalendarDate {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = partsOf(s);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

/**
 * The instant at a wall-clock time on a calendar date in a zone. Uses the zone offset in
 * effect at that moment, so it is correct across DST transitions for all but the skipped hour.
 */
export function instantAt(
  date: CalendarDate,
  hour: number,
  minute: number,
  timeZone: string,
): Instant {
  const [y, m, d] = partsOf(date);
  const guess = Date.UTC(y, m - 1, d, hour, minute);
  const offset = zoneOffsetMs(new Date(guess), timeZone);
  const first = guess - offset;
  // Re-check with the offset at the candidate instant in case a DST boundary lies between.
  const offset2 = zoneOffsetMs(new Date(first), timeZone);
  return new Date(guess - offset2).toISOString();
}

const offsetFormatters = new Map<string, Intl.DateTimeFormat>();
function zoneOffsetMs(at: Date, timeZone: string): number {
  let f = offsetFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    offsetFormatters.set(timeZone, f);
  }
  const p: Record<string, number> = {};
  for (const part of f.formatToParts(at)) {
    if (part.type !== "literal") p[part.type] = Number(part.value);
  }
  const asUtc = Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!);
  return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}

/** Local wall-clock hour and minute of an instant in a zone. */
export function wallClockOf(instant: Instant, timeZone: string): { hour: number; minute: number } {
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    hour: "2-digit",
    minute: "2-digit",
  });
  const p: Record<string, number> = {};
  for (const part of f.formatToParts(new Date(instant))) {
    if (part.type !== "literal") p[part.type] = Number(part.value);
  }
  return { hour: p.hour!, minute: p.minute! };
}
